import React, { useState, useEffect } from 'react';
import { Crown, TrendingUp, Clock, Filter, Star } from 'lucide-react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase-config';
import { calculateDealScore, getDealBadge } from '../services/aiTransformService';

export default function InvestorDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, hot, vip
  const [userTier, setUserTier] = useState('free');

  useEffect(() => {
    loadProjects();
    checkUserTier();
  }, [filter]);

  const checkUserTier = async () => {
    if (!auth.currentUser) return;
    
    // Check user's investor tier from Firestore
    // For now, mock it
    setUserTier('premium'); // or 'pro', 'free'
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      // Query AI properties from Firestore
      let q = query(
        collection(db, 'ai_properties'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);
      const projectsData = snapshot.docs.map(doc => {
        const data = doc.data();
        const dealScore = calculateDealScore(data.propertyData, data.transformation.priceImpact);
        const badge = getDealBadge(dealScore, data.transformation.priceImpact.increase);
        
        return {
          id: doc.id,
          ...data,
          dealScore,
          badge
        };
      });

      // Filter by badge type if needed
      let filtered = projectsData;
      if (filter === 'hot') {
        filtered = projectsData.filter(p => p.badge?.type === 'HOT');
      } else if (filter === 'vip') {
        filtered = projectsData.filter(p => p.badge?.type === 'VIP' || p.badge?.type === 'HOT');
      }

      // Sort by deal score
      filtered.sort((a, b) => b.dealScore - a.dealScore);

      setProjects(filtered);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const ProjectCard = ({ project }) => {
    const { propertyData, transformation, dealScore, badge } = project;
    const profit = transformation.priceImpact.increase;
    const profitMillions = (profit / 1000000).toFixed(1);

    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:border-secondary/50 transition-all duration-300 group">
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={transformation.transformedImages[0] || '/placeholder-house.jpg'} 
            alt={propertyData.address}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          
          {/* Badge */}
          {badge && (
            <div className={`absolute top-3 right-3 px-3 py-1 ${badge.color} ${badge.textColor} text-xs font-bold rounded-full shadow-lg`}>
              {badge.label}
            </div>
          )}

          {/* Deal Score */}
          <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full">
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
              <span className="text-white text-xs font-bold">{dealScore}/100</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Location */}
          <h3 className="text-white font-bold text-lg mb-2">
            {propertyData.address}
          </h3>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
            <div>
              <div className="text-gray-400">Telek méret</div>
              <div className="text-white font-semibold">{propertyData.area_m2} m²</div>
            </div>
            <div>
              <div className="text-gray-400">Stílus</div>
              <div className="text-white font-semibold">{transformation.selectedStyle}</div>
            </div>
          </div>

          {/* Price Box */}
          <div className="bg-black/30 rounded-lg p-3 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm">Teljes költség</span>
              <span className="text-white font-bold">{(transformation.priceImpact.originalPrice / 1000000).toFixed(1)}M Ft</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm">Piaci érték</span>
              <span className="text-white font-bold">{(transformation.priceImpact.newPrice / 1000000).toFixed(1)}M Ft</span>
            </div>
            <div className="border-t border-white/10 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-secondary text-sm font-bold">Potenciál</span>
                <span className="text-secondary text-xl font-black">+{profitMillions}M Ft</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button className="w-full bg-gradient-to-r from-secondary to-red-600 hover:from-red-600 hover:to-secondary text-white font-bold py-3 rounded-lg transition-all duration-300 transform hover:scale-105">
            Részletek & Foglalás
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Page Header */}
      <div className="border-b border-white/[0.06] px-6 py-5">
        <div className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-yellow-400" />
          <div>
            <h1 className="text-2xl font-black text-white">Befektetői Dashboard</h1>
            <p className="text-[#9ca3af] text-sm mt-0.5">Korai hozzáférés a legjobb ingatlan projektekhez</p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* User Tier Display */}
        <div className="bg-[#161616] border border-white/[0.07] rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-yellow-400" />
              <div>
                <div className="text-white font-bold">
                  {userTier === 'premium' && 'PREMIUM Befektető'}
                  {userTier === 'pro' && 'PRO Befektető'}
                  {userTier === 'free' && 'Ingyenes hozzáférés'}
                </div>
                <div className="text-gray-400 text-sm">
                  {userTier === 'premium' && 'Korlátlan hozzáférés + elsőbbség'}
                  {userTier === 'pro' && 'Heti top 10 projekt'}
                  {userTier === 'free' && 'Csak publikus projektek'}
                </div>
              </div>
            </div>
            {userTier === 'free' && (
              <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all">
                Upgrade
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
              filter === 'all'
                ? 'bg-secondary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            Összes projekt
          </button>
          <button
            onClick={() => setFilter('hot')}
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
              filter === 'hot'
                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            🔥 HOT Dealek
          </button>
          <button
            onClick={() => setFilter('vip')}
            className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all ${
              filter === 'vip'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            💎 VIP Projektek
          </button>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
            <p className="text-gray-400 mt-4">Projektek betöltése...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Még nincsenek elérhető projektek</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
